import { QueryCache, QueryClient, QueryFunction } from "@tanstack/react-query";
import { resolveApiUrl } from "@/lib/api-url";
import { toast } from "@/hooks/use-toast";
import { createSessionExpirationHandler } from "@/lib/session-expiration";

let handleExpiredSession: () => void = () => undefined;

export async function apiFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const response = await fetch(input, init);
  if (response.status !== 401) {
    return response;
  }

  handleExpiredSession();
  return await new Promise<Response>(() => undefined);
}

async function extractHttpErrorMessage(res: Response) {
  const text = (await res.text()).trim();
  if (!text) {
    return res.statusText || `HTTP ${res.status}`;
  }

  try {
    const parsed = JSON.parse(text) as unknown;
    if (parsed && typeof parsed === "object") {
      const message = "message" in parsed ? (parsed as { message?: unknown }).message : null;
      if (typeof message === "string" && message.trim()) {
        return message.trim();
      }
    }
  } catch {
    // Fall back to the raw response body.
  }

  return text;
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const message = await extractHttpErrorMessage(res);
    throw new Error(message);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const res = await apiFetch(resolveApiUrl(url), {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await apiFetch(resolveApiUrl(queryKey.join("/") as string), {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

const QUERY_ERROR_TOAST_COOLDOWN_MS = 6000;
const queryErrorToastShownAt = new Map<string, number>();

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Notify only when the query has no usable data and the failure impacts the current UI.
      if (typeof query.state.data !== "undefined") {
        return;
      }

      const now = Date.now();
      const lastShownAt = queryErrorToastShownAt.get(query.queryHash) ?? 0;
      if (now - lastShownAt < QUERY_ERROR_TOAST_COOLDOWN_MS) {
        return;
      }
      queryErrorToastShownAt.set(query.queryHash, now);

      const description =
        error instanceof Error
          ? error.message
          : "Une erreur est survenue pendant le chargement des données.";

      toast({
        variant: "destructive",
        title: "Erreur de chargement",
        description,
      });
    },
  }),
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});

handleExpiredSession = createSessionExpirationHandler({
  getCurrentPath: () =>
    typeof window === "undefined" ? "/login" : window.location.pathname,
  clearClientSession: () => {
    void queryClient.cancelQueries();
    queryClient.setQueryData(["auth-session"], null);
    queryClient.removeQueries({
      predicate: (query) => query.queryKey[0] !== "auth-session",
    });
  },
  revokeServerSession: async () => {
    const { signOut } = await import("@/lib/auth-client");
    await signOut();
  },
  replaceLocation: (path) => window.location.replace(path),
});
